import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { EmdKpi } from '../emd-mock-data';

/**
 * KPI tile for the EMD dashboard, modelled on the app's KPI widget
 * (widget/kpi-chart, `app-kpi-chart`): same markup, class names, styling and
 * sparkline treatment, so the tiles read as part of the same design system.
 *
 * It is not that component. `app-kpi-chart` is API-driven — it calls
 * setLoading(true) then returns early when item.config has no kpi_1_value_api,
 * so with static inputs it never leaves its loading state. This dashboard is a
 * static prototype, so the presentation is mirrored without the data plumbing.
 * When EMD is wired to real APIs, this should be replaced by `app-kpi-chart`.
 */
@Component({
    selector: 'emd-kpi-card',
    templateUrl: './emd-kpi-card.component.html',
    styleUrls: ['./emd-kpi-card.component.scss'],
    standalone: false
})
export class EmdKpiCardComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('svgContainer') svgContainer!: ElementRef<HTMLDivElement>;

  @Input() kpi!: EmdKpi;

  private drawn = false;
  private resizeObserver: ResizeObserver | null = null;
  private resizeDebounce: any = null;

  // Mirrors kpi-chart's sparkline geometry.
  private readonly SPARKLINE_PADDING = { top: 8, right: 8, bottom: 8, left: 8 };
  private readonly MIN_CHART_WIDTH = 80;
  private readonly MIN_CHART_HEIGHT = 40;

  ngAfterViewInit(): void {
    this.drawn = true;
    this.render();

    // Re-render on container resize, as kpi-chart does, so the sparkline
    // tracks the tile instead of keeping a stale width.
    if (this.svgContainer?.nativeElement) {
      this.resizeObserver = new ResizeObserver(() => {
        clearTimeout(this.resizeDebounce);
        this.resizeDebounce = setTimeout(() => this.render(), 100);
      });
      this.resizeObserver.observe(this.svgContainer.nativeElement);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['kpi'] && this.drawn) { this.render(); }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    clearTimeout(this.resizeDebounce);
    const tip = document.querySelector('.emd-kpi-tooltip') as HTMLElement | null;
    if (tip) { tip.style.opacity = '0'; }
  }

  /**
   * Colour comes from whether the movement is good, not from its direction, so
   * a fall in returns reads green. Matches kpi-chart's trend-* classes.
   */
  get trendClass(): string {
    if (this.kpi?.direction === 'FLAT') { return 'trend-flat'; }
    return this.kpi?.positive ? 'trend-up' : 'trend-down';
  }

  /** Arrow follows the direction. kpi-chart uses glyphs, not icon fonts. */
  get trendIcon(): string {
    if (this.kpi?.direction === 'UP') { return '▲'; }
    if (this.kpi?.direction === 'DOWN') { return '▼'; }
    return '';
  }

  /** Typed createElementNS: the literal namespace keeps the SVG element types. */
  private svgEl<K extends keyof SVGElementTagNameMap>(tag: K): SVGElementTagNameMap[K] {
    return document.createElementNS('http://www.w3.org/2000/svg', tag);
  }

  private getChartSize(): { w: number; h: number } {
    const el = this.svgContainer?.nativeElement;
    const w = el ? Math.max(el.clientWidth || el.offsetWidth, this.MIN_CHART_WIDTH) : 100;
    const h = el ? Math.max(el.clientHeight || el.offsetHeight, this.MIN_CHART_HEIGHT) : 44;
    return { w, h };
  }

  private render(): void {
    const host = this.svgContainer?.nativeElement;
    const data = this.kpi?.sparkline;
    if (!host || !data?.length) { return; }

    host.querySelector('svg')?.remove();

    const { w: chartW, h: chartH } = this.getChartSize();
    const pad = this.SPARKLINE_PADDING;
    const width = chartW - pad.left - pad.right;
    const height = chartH - pad.top - pad.bottom;

    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const color = this.kpi.color || '#2979FF';

    const svg = this.svgEl('svg');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');
    svg.setAttribute('viewBox', `0 0 ${chartW} ${chartH}`);
    host.appendChild(svg);

    // Unique per instance so sibling tiles don't share filter ids.
    const glowId = `emdSparkGlow-${Math.random().toString(36).slice(2, 9)}`;
    svg.appendChild(this.buildGlowFilter(glowId));

    const points = data.map((d, i) => ({
      x: pad.left + (width / (data.length - 1)) * i,
      y: pad.top + height - ((d - min) / range) * height,
      value: d,
    }));

    // Tick marks along the baseline, as in kpi-chart.
    const axisY = pad.top + height;
    points.forEach((p) => {
      const tick = this.svgEl('line');
      tick.setAttribute('x1', String(p.x));
      tick.setAttribute('y1', String(axisY));
      tick.setAttribute('x2', String(p.x));
      tick.setAttribute('y2', String(axisY + 4));
      tick.setAttribute('stroke', '#d1d5db');
      tick.setAttribute('stroke-width', '1');
      svg.appendChild(tick);
    });

    const path = this.svgEl('path');
    path.setAttribute('d', this.catmullRomPath(points));
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', color);
    path.setAttribute('stroke-width', '3');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('filter', `url(#${glowId})`);
    svg.appendChild(path);

    this.attachTooltip(svg, points, chartH, color);
  }

  private buildGlowFilter(id: string): SVGDefsElement {
    const defs = this.svgEl('defs');
    const filter = this.svgEl('filter');
    filter.setAttribute('id', id);
    filter.setAttribute('x', '-50%');
    filter.setAttribute('y', '-50%');
    filter.setAttribute('width', '200%');
    filter.setAttribute('height', '200%');

    const blur = this.svgEl('feGaussianBlur');
    blur.setAttribute('in', 'SourceGraphic');
    blur.setAttribute('stdDeviation', '2');
    blur.setAttribute('result', 'blur');

    const offset = this.svgEl('feOffset');
    offset.setAttribute('in', 'blur');
    offset.setAttribute('dx', '0');
    offset.setAttribute('dy', '1');
    offset.setAttribute('result', 'offsetBlur');

    const merge = this.svgEl('feMerge');
    const m1 = this.svgEl('feMergeNode');
    m1.setAttribute('in', 'offsetBlur');
    const m2 = this.svgEl('feMergeNode');
    m2.setAttribute('in', 'SourceGraphic');
    merge.appendChild(m1);
    merge.appendChild(m2);

    filter.appendChild(blur);
    filter.appendChild(offset);
    filter.appendChild(merge);
    defs.appendChild(filter);
    return defs;
  }

  /** Same Catmull-Rom → Bezier conversion kpi-chart uses for its sparkline. */
  private catmullRomPath(points: Array<{ x: number; y: number }>): string {
    if (points.length < 2) { return ''; }
    if (points.length === 2) {
      return `M ${points[0].x},${points[0].y} L ${points[1].x},${points[1].y}`;
    }

    const tension = 0.5;
    let path = `M ${points[0].x},${points[0].y}`;

    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[Math.max(0, i - 1)];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[Math.min(points.length - 1, i + 2)];

      const cp1x = p1.x + ((p2.x - p0.x) / 6) * tension;
      const cp1y = p1.y + ((p2.y - p0.y) / 6) * tension;
      const cp2x = p2.x - ((p3.x - p1.x) / 6) * tension;
      const cp2y = p2.y - ((p3.y - p1.y) / 6) * tension;

      path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
    }

    return path;
  }

  private attachTooltip(
    svg: SVGSVGElement,
    points: Array<{ x: number; y: number; value: number }>,
    chartH: number,
    color: string
  ): void {
    const tip = this.tooltipEl();

    const guide = this.svgEl('line');
    guide.setAttribute('stroke', color);
    guide.setAttribute('stroke-width', '1.5');
    guide.setAttribute('stroke-dasharray', '4,3');
    guide.setAttribute('opacity', '0');
    svg.appendChild(guide);

    const dot = this.svgEl('circle');
    dot.setAttribute('r', '3.5');
    dot.setAttribute('fill', '#fff');
    dot.setAttribute('stroke', color);
    dot.setAttribute('stroke-width', '2');
    dot.setAttribute('opacity', '0');
    svg.appendChild(dot);

    const band = (points[1]?.x ?? 20) - (points[0]?.x ?? 0);

    points.forEach((p) => {
      const hit = this.svgEl('rect');
      hit.setAttribute('x', String(p.x - band / 2));
      hit.setAttribute('y', '0');
      hit.setAttribute('width', String(Math.max(band, 6)));
      hit.setAttribute('height', String(chartH));
      hit.setAttribute('fill', 'transparent');
      hit.style.cursor = 'pointer';

      hit.addEventListener('mouseenter', (event: MouseEvent) => {
        guide.setAttribute('x1', String(p.x));
        guide.setAttribute('y1', '0');
        guide.setAttribute('x2', String(p.x));
        guide.setAttribute('y2', String(chartH));
        guide.setAttribute('opacity', '1');
        dot.setAttribute('cx', String(p.x));
        dot.setAttribute('cy', String(p.y));
        dot.setAttribute('opacity', '1');

        tip.innerHTML =
          `<div class="emd-kpi-tip-title">${this.kpi.title}</div>` +
          `<div class="emd-kpi-tip-value">${p.value.toLocaleString('en-US')}</div>`;
        tip.style.left = `${event.clientX + 14}px`;
        tip.style.top = `${event.clientY - 46}px`;
        tip.style.opacity = '1';
      });

      hit.addEventListener('mousemove', (event: MouseEvent) => {
        tip.style.left = `${event.clientX + 14}px`;
        tip.style.top = `${event.clientY - 46}px`;
      });

      hit.addEventListener('mouseleave', () => {
        guide.setAttribute('opacity', '0');
        dot.setAttribute('opacity', '0');
        tip.style.opacity = '0';
      });

      svg.appendChild(hit);
    });
  }

  /**
   * Single shared tooltip node, mirroring kpi-chart's approach. Styled inline
   * because the node lives on document.body, where this component's emulated
   * styles cannot reach it.
   */
  private tooltipEl(): HTMLElement {
    let tip = document.querySelector('.emd-kpi-tooltip') as HTMLElement | null;
    if (!tip) {
      tip = document.createElement('div');
      tip.className = 'emd-kpi-tooltip';
      Object.assign(tip.style, {
        position: 'fixed',
        zIndex: '10000',
        pointerEvents: 'none',
        opacity: '0',
        transition: 'opacity 0.15s ease',
        background: '#1e2430',
        color: '#fff',
        borderRadius: '8px',
        padding: '6px 10px',
        fontFamily: 'Poppins',
        fontSize: '11px',
        lineHeight: '1.45',
        boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
        whiteSpace: 'nowrap',
      } as CSSStyleDeclaration);
      document.body.appendChild(tip);
    }
    return tip;
  }
}
